import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { MAX_PULL, RESISTANCE, THRESHOLD } from "../constants";

type PullState = "idle" | "pulling" | "ready" | "refreshing";

export const usePullToRefresh = (queryKeys: readonly unknown[][]) => {
  const queryClient = useQueryClient();
  const [pullDistance, setPullDistance] = useState(0);
  const [state, setState] = useState<PullState>("idle");

  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const stateRef = useRef<PullState>("idle");

  stateRef.current = state;

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0 || stateRef.current === "refreshing") return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current || stateRef.current === "refreshing") return;

      const delta = e.touches[0].clientY - startYRef.current;

      if (delta <= 0) {
        if (pullDistance !== 0) setPullDistance(0);
        if (stateRef.current !== "idle") setState("idle");
        return;
      }

      if (window.scrollY > 0) {
        pullingRef.current = false;
        setPullDistance(0);
        setState("idle");
        return;
      }

      const distance = Math.min(delta * RESISTANCE, MAX_PULL);
      setPullDistance(distance);
      setState(distance >= THRESHOLD ? "ready" : "pulling");
    };

    const onTouchEnd = async () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;

      if (stateRef.current !== "ready") {
        setPullDistance(0);
        setState("idle");
        return;
      }

      setState("refreshing");
      setPullDistance(THRESHOLD);

      try {
        await Promise.all(
          queryKeys.map((key) =>
            queryClient.invalidateQueries({ queryKey: key }),
          ),
        );
      } finally {
        setPullDistance(0);
        setState("idle");
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [queryClient, queryKeys, pullDistance]);

  return {
    pullDistance,
    isRefreshing: state === "refreshing",
    isReady: state === "ready",
    isPulling: state === "pulling",
  };
};
